import { storage } from "../firebase"

/**
 * Load the full body of a communication from Cloud Storage and return it as a
 * string. Use this when you want to render the body inline (e.g. inside a
 * sandboxed iframe via `srcdoc`).
 *
 * Reads are gated by the `communications/{commID}/{path=**}` rule in
 * `storage.rules`: only authenticated users with `is_admin: true` on their
 * user doc can fetch these.
 *
 * @param {string} bodyStoragePath The `body_storage_path` field from a
 *   `communications/{id}` document, e.g. `"communications/abc123/body.html"`.
 *   Returns "" for documents that don't have a body (e.g. unsupported call
 *   recordings, or messages where Graph returned no body).
 * @returns {Promise<string>} The raw HTML or plain-text body. Caller is
 *   responsible for sanitisation if rendering into the DOM.
 */
export async function loadCommunicationBody(bodyStoragePath) {
	if (!bodyStoragePath) return ""

	const ref = storage.ref(bodyStoragePath)
	const blob = await ref.getDownloadURL().then((url) => fetch(url)).then((res) => {
		if (!res.ok) throw new Error(`Storage fetch failed: ${res.status} ${res.statusText}`)
		return res.text()
	})

	return blob
}

/**
 * Get a signed download URL for a communication body without fetching the
 * bytes. Useful when you'd rather hand the URL to an `<iframe src>` or to a
 * download link, and let the browser handle the bytes.
 *
 * The URL is a Firebase signed URL with a long-lived token tied to the path —
 * it doesn't expire on a short timeout, but it does become invalid if the
 * underlying object is deleted or the access token regenerated.
 */
export async function getCommunicationBodyUrl(bodyStoragePath) {
	if (!bodyStoragePath) return ""
	return storage.ref(bodyStoragePath).getDownloadURL()
}
